import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { MenuEntry } from "../canvas/controller";

/** A QMenu popped up at the pointer: closes on a click elsewhere, Esc, scroll, resize or when the window loses focus. */
export function ContextMenu({ x, y, items, onClose }: { x: number; y: number; items: MenuEntry[]; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: x, top: y });

  useEffect(() => { ref.current?.querySelector<HTMLButtonElement>("button:not(:disabled)")?.focus(); }, []);

  useLayoutEffect(() => {                                  // keep it on screen
    const r = ref.current!.getBoundingClientRect();
    setPos({ left: Math.max(4, Math.min(x, window.innerWidth - r.width - 4)), top: Math.max(4, Math.min(y, window.innerHeight - r.height - 4)) });
  }, [x, y]);

  useEffect(() => {
    const away = (e: Event) => { if (!ref.current?.contains(e.target as Node)) onClose(); };
    const esc = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.stopPropagation(); onClose(); return; }
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;       // a QMenu is driven from the keyboard too
      const buttons = [...ref.current!.querySelectorAll<HTMLButtonElement>("button:not(:disabled)")];
      if (!buttons.length) return;
      e.preventDefault(); e.stopPropagation();
      const at = buttons.indexOf(document.activeElement as HTMLButtonElement);
      buttons[(at + (e.key === "ArrowDown" ? 1 : -1) + buttons.length) % buttons.length].focus();
    };
    window.addEventListener("mousedown", away, true);
    window.addEventListener("keydown", esc, true);
    window.addEventListener("blur", onClose);
    window.addEventListener("resize", onClose);
    window.addEventListener("wheel", onClose, { passive: true });
    return () => {
      window.removeEventListener("mousedown", away, true); window.removeEventListener("keydown", esc, true);
      window.removeEventListener("blur", onClose); window.removeEventListener("resize", onClose); window.removeEventListener("wheel", onClose);
    };
  }, [onClose]);

  return (
    <div className="menu" role="menu" ref={ref} data-testid="context-menu" style={{ position: "fixed", left: pos.left, top: pos.top, zIndex: 50 }}>
      {items.map((it, i) => it.separator ? <div key={i} className="menu-sep" role="separator" /> : (
        <button key={it.label} className="menu-item" role="menuitem" disabled={it.disabled} data-testid={`ctx-${it.label}`}
          onClick={() => { onClose(); it.run?.(); }}>
          <span>{it.label}</span><span className="key">{it.hint ?? ""}</span>
        </button>
      ))}
    </div>
  );
}
